"use client";

import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { supabaseService } from '@/lib/supabaseService';
import {
  ConsentBucket,
  ConsentState,
  EMPTY_CONSENT,
} from '@/types/consent';
import {
  fetchConsent,
  recordConsent,
  bumpActiveDay,
} from '@/lib/consent/consentService';
import { identifyUser, resetIdentity, track } from '@/lib/telemetry/posthog';

interface ConsentContextType {
  /** Current consent state. Falsy buckets = not granted (or unknown until loaded). */
  consent: ConsentState;
  /** Auth + consent + profile-onboarding-flag loaded; UI can decide to gate. */
  loaded: boolean;
  /**
   * Authenticated user's onboarding status. Null while loading or signed out.
   * Drives the "render nothing until we know" gate that prevents a dashboard
   * flash before OnboardingOverlay can decide to mount on top.
   */
  hasOnboarded: boolean | null;
  /**
   * Whether the authenticated user has already been through the guided tour.
   * Null while loading or signed out. Read by the tour's auto-start so a
   * returning user is never walked through the product a second time.
   */
  hasToured: boolean | null;
  /** True iff user is authenticated AND has no Bucket C consent row yet (backfill only). */
  needsFirstRunConsent: boolean;
  /** True iff user has Bucket C but has never been asked about A or B yet. */
  needsBucketABDecision: boolean;
  /** Flip a single bucket; appends a new audit row. */
  setConsent: (bucket: ConsentBucket, granted: boolean) => Promise<void>;
  /**
   * Optimistically flip the onboarding flag locally. Call from OnboardingOverlay
   * once `completeOnboarding()` resolves so ConsentGate re-evaluates without
   * a round-trip / race against profile cache invalidation.
   */
  markOnboarded: () => void;
  /** Optimistic twin of markOnboarded for the tour's completion. */
  markToured: () => void;
  /** Force-refresh from DB (after server-side mutations). */
  refresh: () => Promise<void>;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userId, setUserId] = useState<string | null>(null);
  const [consent, setConsentState] = useState<ConsentState>(EMPTY_CONSENT);
  const [hasOnboarded, setHasOnboarded] = useState<boolean | null>(null);
  const [hasToured, setHasToured] = useState<boolean | null>(null);
  const [loaded, setLoaded] = useState(false);
  const bumpedRef = useRef(false);
  // Monotonic counter: every loadAll captures its id at start; resolutions
  // whose id no longer matches the latest are stale (user switched mid-flight)
  // and dropped to avoid contaminating fresh state with the previous user's data.
  const loadIdRef = useRef(0);

  const loadAll = useCallback(async (uid: string) => {
    const id = ++loadIdRef.current;
    // Profile is cached in supabaseService for 5s, so Layout's later
    // getProfile() reads from cache instead of the network. Profile may be
    // null right after signup if the DB trigger hasn't fired yet — leave
    // hasOnboarded as null so gates stay neutral rather than pinning the
    // user behind an overlay that has nothing to update.
    const [c, profile] = await Promise.all([
      fetchConsent(uid),
      supabaseService.getProfile(),
    ]);
    if (id !== loadIdRef.current) return;
    setConsentState(c);
    setHasOnboarded(profile ? Boolean(profile.has_onboarded) : null);
    setHasToured(profile ? Boolean(profile.has_toured) : null);
    setLoaded(true);
  }, []);

  // Track auth state. Re-load consent on user change.
  useEffect(() => {
    let active = true;
    supabase.auth.getUser().then(({ data }) => {
      if (!active) return;
      const uid = data.user?.id ?? null;
      setUserId(uid);
      if (uid) {
        loadAll(uid);
      } else {
        setLoaded(true);
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      // INITIAL_SESSION duplicates the getUser() call above; TOKEN_REFRESHED
      // fires hourly and would re-run consent + profile fetches for no reason.
      if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') return;
      const uid = session?.user?.id ?? null;
      setUserId(uid);
      if (uid) {
        bumpedRef.current = false;
        loadAll(uid);
      } else {
        // Invalidate any in-flight loadAll so it can't overwrite EMPTY_CONSENT
        // with the previous user's data after sign-out.
        loadIdRef.current++;
        setConsentState(EMPTY_CONSENT);
        setHasOnboarded(null);
        setHasToured(null);
        setLoaded(true);
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [loadAll]);

  // Bump active-day once per session, only after Bucket C is granted.
  // (Pre-consent, we cannot legally write engagement rows tied to identity.)
  useEffect(() => {
    if (!userId || !loaded || bumpedRef.current) return;
    if (!consent.C) return;
    bumpedRef.current = true;
    bumpActiveDay().catch(() => {
      bumpedRef.current = false;
    });
  }, [userId, loaded, consent.C]);

  // Pseudonymous PostHog identify gated on Bucket A. Supabase UUID is opaque
  // to PostHog so no PII leaves the app; this just lets MAU/retention reflect
  // real humans across tab/session boundaries. resetIdentity on signout
  // prevents the next account on the same browser from merging.
  useEffect(() => {
    if (!loaded || !consent.A) return;
    if (userId) {
      identifyUser(userId);
    } else {
      resetIdentity();
    }
  }, [userId, consent.A, loaded]);

  const setConsent = useCallback(
    async (bucket: ConsentBucket, granted: boolean) => {
      if (!userId) throw new Error('Cannot set consent: not authenticated');
      await recordConsent(userId, bucket, granted);
      // Fires after the row lands, so a failed write never reports a decision
      // the database does not hold. Dropped entirely while telemetry is off,
      // which is every decision except the one that turns it on.
      track('consent_decided', { bucket, granted });
      setConsentState((prev) => {
        const next: ConsentState = { ...prev, [bucket]: granted, loadedAt: Date.now() };
        if (bucket === 'A') next.decidedA = true;
        if (bucket === 'B') next.decidedB = true;
        return next;
      });
    },
    [userId],
  );

  const markOnboarded = useCallback(() => {
    setHasOnboarded(true);
  }, []);

  const markToured = useCallback(() => {
    setHasToured(true);
  }, []);

  const refresh = useCallback(async () => {
    if (!userId) return;
    await loadAll(userId);
  }, [userId, loadAll]);

  const needsFirstRunConsent = Boolean(userId && loaded && !consent.C);
  // C must be granted first; A/B prompt only fires once C is in place.
  const needsBucketABDecision = Boolean(
    userId && loaded && consent.C && (!consent.decidedA || !consent.decidedB),
  );

  // Memoize so consumers (ConsentGate, OnboardingOverlay, Layout, future
  // tour code) don't re-render on every unrelated parent update.
  const value = useMemo<ConsentContextType>(() => ({
    consent,
    loaded,
    hasOnboarded,
    hasToured,
    needsFirstRunConsent,
    needsBucketABDecision,
    setConsent,
    markOnboarded,
    markToured,
    refresh,
  }), [
    consent,
    loaded,
    hasOnboarded,
    hasToured,
    needsFirstRunConsent,
    needsBucketABDecision,
    setConsent,
    markOnboarded,
    markToured,
    refresh,
  ]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};
